export const HOUSEHOLD_TASK_FREQUENCIES = [
  "Daily",
  "Twice Daily",
  "Thrice Daily",
  "Weekly",
  "Twice Weekly",
  "Thrice Weekly",
  "Monthly",
  "Twice Monthly",
  "Quarterly",
] as const;

export type HouseholdTaskFrequency =
  (typeof HOUSEHOLD_TASK_FREQUENCIES)[number];

export type HouseholdTaskRow = {
  id: string;
  responsibility: string;
  capable: string;
  category: string;
  task: string;
  type: string;
  blockedBy: string;
  frequency: HouseholdTaskFrequency;
  minutes: number;
  decimal: number;
  total: number;
  notes: string;
};

export type HouseholdTaskDimensionLists = {
  people: string[];
  categories: string[];
  taskNames: string[];
  types: string[];
};

export type HouseholdChartMetric =
  | "responsibility"
  | "category"
  | "type"
  | "frequency"
  | "capable";

export type HouseholdChartCard = {
  id: string;
  metric: HouseholdChartMetric;
  chartType: "pie" | "bar";
  title: string;
  color: string;
};

export type HouseholdChartProfile = {
  id: string;
  name: string;
  cards: HouseholdChartCard[];
};

export type HouseholdTaskFilterableColumn =
  | "responsibility"
  | "capable"
  | "category"
  | "task"
  | "type"
  | "blockedBy"
  | "frequency";

export type HouseholdTaskFiltersState = Partial<
  Record<HouseholdTaskFilterableColumn, string[]>
>;

export type HouseholdTableViewProfile = {
  id: string;
  name: string;
  search: string;
  filters: HouseholdTaskFiltersState;
};

export type HouseholdTasksToolSettingsV3 = {
  version: 3;
  rows: HouseholdTaskRow[];
  lists: HouseholdTaskDimensionLists;
  chartProfiles: HouseholdChartProfile[];
  activeChartProfileId: string;
  tableProfiles: HouseholdTableViewProfile[];
  activeTableProfileId: string;
};

export type HouseholdTasksToolSettingsV2 = {
  version: 2;
  rows: HouseholdTaskRow[];
  lists: HouseholdTaskDimensionLists;
  chartProfiles: HouseholdChartProfile[];
  activeChartProfileId: string;
};

export type HouseholdTasksToolSettingsV1 = {
  version: 1;
  rows: HouseholdTaskRow[];
  lists: HouseholdTaskDimensionLists;
};

const DEFAULT_LISTS: HouseholdTaskDimensionLists = {
  people: [],
  categories: [],
  taskNames: [],
  types: [],
};

const DEFAULT_FREQUENCY: HouseholdTaskFrequency = "Weekly";
const DEFAULT_CHART_PROFILE_ID = "default-dashboard";
const DEFAULT_TABLE_PROFILE_ID = "default-view";

const roundToHundredths = (value: number) =>
  Math.round((Number.isFinite(value) ? value : 0) * 100) / 100;

const toNumber = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const createTaskRowId = () =>
  `household-task-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

export const createChartEntityId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

export const createTableViewEntityId = () =>
  createChartEntityId("household-view");

export const calculateDecimalFromMinutes = (minutes: number) =>
  roundToHundredths(toNumber(minutes) / 60);

export const calculateTotalFromMinutes = (minutes: number) =>
  calculateDecimalFromMinutes(minutes);

export const formatHours = (value: number) => {
  const rounded = roundToHundredths(value);
  if (Number.isInteger(rounded)) return `${rounded}`;
  return rounded.toFixed(2).replace(/0$/, "").replace(/\.00$/, "");
};

export const splitPeople = (value: string) =>
  value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

export const sortUnique = (values: string[]) =>
  Array.from(
    new Set(values.map((value) => String(value || "").trim()).filter(Boolean))
  ).sort((left, right) => left.localeCompare(right));

export const normalizeHouseholdTaskRow = (
  input?: Partial<HouseholdTaskRow>
): HouseholdTaskRow => {
  const minutes = Math.max(0, roundToHundredths(toNumber(input?.minutes)));
  const normalizedFrequency = HOUSEHOLD_TASK_FREQUENCIES.includes(
    input?.frequency as HouseholdTaskFrequency
  )
    ? (input?.frequency as HouseholdTaskFrequency)
    : DEFAULT_FREQUENCY;

  return {
    id: input?.id || createTaskRowId(),
    responsibility: String(input?.responsibility || "").trim(),
    capable: String(input?.capable || "").trim(),
    category: String(input?.category || "").trim(),
    task: String(input?.task || "").trim(),
    type: String(input?.type || "").trim(),
    blockedBy: String(input?.blockedBy || "").trim(),
    frequency: normalizedFrequency,
    minutes,
    decimal: calculateDecimalFromMinutes(minutes),
    total: calculateTotalFromMinutes(minutes),
    notes: String(input?.notes || "").trim(),
  };
};

const parseCsvRows = (text: string) => {
  const rows: string[][] = [];
  let currentValue = "";
  let currentRow: string[] = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const nextCharacter = text[index + 1];

    if (character === '"') {
      if (inQuotes && nextCharacter === '"') {
        currentValue += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (character === "," && !inQuotes) {
      currentRow.push(currentValue);
      currentValue = "";
      continue;
    }

    if ((character === "\n" || character === "\r") && !inQuotes) {
      if (character === "\r" && nextCharacter === "\n") {
        index += 1;
      }
      currentRow.push(currentValue);
      if (currentRow.some((cell) => cell.trim().length > 0)) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentValue = "";
      continue;
    }

    currentValue += character;
  }

  if (currentValue.length > 0 || currentRow.length > 0) {
    currentRow.push(currentValue);
    if (currentRow.some((cell) => cell.trim().length > 0)) {
      rows.push(currentRow);
    }
  }

  return rows;
};

export const parseHouseholdTasksCsv = (text: string): HouseholdTaskRow[] => {
  const rows = parseCsvRows(text);
  const [, ...dataRows] = rows;
  return dataRows.map((row) =>
    normalizeHouseholdTaskRow({
      responsibility: row[0],
      capable: row[1],
      category: row[2],
      task: row[3],
      type: row[4],
      blockedBy: row[5],
      frequency: row[6] as HouseholdTaskFrequency,
      minutes: toNumber(row[7]),
      notes: row[10],
    })
  );
};

export const deriveDimensionLists = (
  rows: HouseholdTaskRow[],
  savedLists?: Partial<HouseholdTaskDimensionLists>
): HouseholdTaskDimensionLists => ({
  people: sortUnique([
    ...(savedLists?.people || []),
    ...rows.map((row) => row.responsibility),
    ...rows.flatMap((row) => splitPeople(row.capable)),
  ]),
  categories: sortUnique([
    ...(savedLists?.categories || []),
    ...rows.map((row) => row.category),
  ]),
  taskNames: sortUnique([
    ...(savedLists?.taskNames || []),
    ...rows.map((row) => row.task),
  ]),
  types: sortUnique([
    ...(savedLists?.types || []),
    ...rows.map((row) => row.type),
  ]),
});

export const createBlankHouseholdTaskRow = (): HouseholdTaskRow =>
  normalizeHouseholdTaskRow({
    frequency: DEFAULT_FREQUENCY,
    minutes: 0,
  });

export const DEFAULT_HOUSEHOLD_CHART_CARDS: HouseholdChartCard[] = [
  {
    id: "chart-responsibility",
    metric: "responsibility",
    chartType: "pie",
    title: "Time Per Person",
    color: "#5dc4b2",
  },
  {
    id: "chart-category",
    metric: "category",
    chartType: "pie",
    title: "Time Per Category",
    color: "#5b8def",
  },
  {
    id: "chart-responsibility-bar",
    metric: "responsibility",
    chartType: "bar",
    title: "Time Per Person",
    color: "#4285F4",
  },
  {
    id: "chart-type",
    metric: "type",
    chartType: "pie",
    title: "Time by Type",
    color: "#f59e0b",
  },
];

export const DEFAULT_HOUSEHOLD_CHART_PROFILES: HouseholdChartProfile[] = [
  {
    id: DEFAULT_CHART_PROFILE_ID,
    name: "Default Dashboard",
    cards: DEFAULT_HOUSEHOLD_CHART_CARDS,
  },
];

export const DEFAULT_HOUSEHOLD_TABLE_PROFILES: HouseholdTableViewProfile[] = [
  {
    id: DEFAULT_TABLE_PROFILE_ID,
    name: "All Tasks",
    search: "",
    filters: {},
  },
];

const getDefaultChartCardPresentation = (
  metric: HouseholdChartMetric,
  chartType: HouseholdChartCard["chartType"]
) => {
  if (metric === "responsibility" && chartType === "bar") {
    return {
      title: "Time Per Person",
      color: "#4285F4",
    };
  }

  if (metric === "responsibility") {
    return {
      title: "Time Per Person",
      color: "#4285F4",
    };
  }

  if (metric === "category") {
    return {
      title: "Time Per Category",
      color: "#EA4335",
    };
  }

  if (metric === "type") {
    return {
      title: "Time by Type",
      color: "#34A853",
    };
  }

  if (metric === "frequency") {
    return {
      title: "Time by Frequency",
      color: "#FBBC05",
    };
  }

  return {
    title: "Time Per Capable Group",
    color: "#7E57C2",
  };
};

export const normalizeChartCard = (
  input?: Partial<HouseholdChartCard>
): HouseholdChartCard => {
  const metric =
    input?.metric === "responsibility" ||
    input?.metric === "category" ||
    input?.metric === "type" ||
    input?.metric === "frequency" ||
    input?.metric === "capable"
      ? input.metric
      : "responsibility";
  const chartType = input?.chartType === "bar" ? "bar" : "pie";
  const defaults = getDefaultChartCardPresentation(metric, chartType);

  return {
    id: input?.id || createChartEntityId("household-chart"),
    metric,
    chartType,
    title: String(input?.title || defaults.title).trim() || defaults.title,
    color: String(input?.color || defaults.color).trim() || defaults.color,
  };
};

export const normalizeChartProfile = (
  input?: Partial<HouseholdChartProfile>
): HouseholdChartProfile => {
  const cards = Array.isArray(input?.cards)
    ? input.cards.map((card) => normalizeChartCard(card))
    : DEFAULT_HOUSEHOLD_CHART_CARDS;

  return {
    id: input?.id || createChartEntityId("household-profile"),
    name:
      String(input?.name || "Custom Dashboard").trim() || "Custom Dashboard",
    cards: cards.length > 0 ? cards : DEFAULT_HOUSEHOLD_CHART_CARDS,
  };
};

const FILTERABLE_COLUMNS: HouseholdTaskFilterableColumn[] = [
  "responsibility",
  "capable",
  "category",
  "task",
  "type",
  "blockedBy",
  "frequency",
];

const normalizeFilters = (input: unknown): HouseholdTaskFiltersState => {
  if (!input || typeof input !== "object") {
    return {};
  }

  const parsed = input as Record<string, unknown>;

  return FILTERABLE_COLUMNS.reduce<HouseholdTaskFiltersState>(
    (filters, column) => {
      const values = parsed[column];
      if (!Array.isArray(values)) {
        return filters;
      }

      const normalizedValues = sortUnique(
        values.map((value) => String(value || "").trim()).filter(Boolean)
      );

      if (normalizedValues.length === 0) {
        return filters;
      }

      filters[column] = normalizedValues;
      return filters;
    },
    {}
  );
};

export const normalizeTableViewProfile = (
  input?: Partial<HouseholdTableViewProfile>
): HouseholdTableViewProfile => ({
  id: input?.id || createTableViewEntityId(),
  name: String(input?.name || "Custom View").trim() || "Custom View",
  search: String(input?.search || "").trim(),
  filters: normalizeFilters(input?.filters),
});

export const normalizeHouseholdTasksToolSettings = (
  input: unknown,
  fallbackRows: HouseholdTaskRow[]
): HouseholdTasksToolSettingsV3 => {
  if (!input || typeof input !== "object") {
    return {
      version: 3,
      rows: fallbackRows,
      lists: deriveDimensionLists(fallbackRows),
      chartProfiles: DEFAULT_HOUSEHOLD_CHART_PROFILES,
      activeChartProfileId: DEFAULT_CHART_PROFILE_ID,
      tableProfiles: DEFAULT_HOUSEHOLD_TABLE_PROFILES,
      activeTableProfileId: DEFAULT_TABLE_PROFILE_ID,
    };
  }

  const parsed = input as Partial<HouseholdTasksToolSettingsV3> &
    Partial<HouseholdTasksToolSettingsV2> &
    Partial<HouseholdTasksToolSettingsV1>;
  const normalizedRows = Array.isArray(parsed.rows)
    ? parsed.rows.map((row) => normalizeHouseholdTaskRow(row))
    : fallbackRows;
  const rows = normalizedRows.length > 0 ? normalizedRows : fallbackRows;
  const chartProfiles = Array.isArray(parsed.chartProfiles)
    ? parsed.chartProfiles.map((profile) => normalizeChartProfile(profile))
    : DEFAULT_HOUSEHOLD_CHART_PROFILES;
  const activeChartProfileId =
    typeof parsed.activeChartProfileId === "string" &&
    chartProfiles.some((profile) => profile.id === parsed.activeChartProfileId)
      ? parsed.activeChartProfileId
      : chartProfiles[0]?.id || DEFAULT_CHART_PROFILE_ID;
  const tableProfiles = Array.isArray(parsed.tableProfiles)
    ? parsed.tableProfiles.map((profile) => normalizeTableViewProfile(profile))
    : DEFAULT_HOUSEHOLD_TABLE_PROFILES;
  const activeTableProfileId =
    typeof parsed.activeTableProfileId === "string" &&
    tableProfiles.some((profile) => profile.id === parsed.activeTableProfileId)
      ? parsed.activeTableProfileId
      : tableProfiles[0]?.id || DEFAULT_TABLE_PROFILE_ID;

  return {
    version: 3,
    rows,
    lists: deriveDimensionLists(rows, parsed.lists || DEFAULT_LISTS),
    chartProfiles,
    activeChartProfileId,
    tableProfiles,
    activeTableProfileId,
  };
};
